import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Users,
  Plus,
  Trash2,
  Edit,
  User as UserIcon,
  UserPlus,
  Power,
} from "lucide-react";
import { recruitingAPI } from "../../services/recruitingApi";
import api from "../../services/api";
import SearchInput from "../../components/SearchInput";
import UserTableSkeleton from "./UserTableSkeleton";
import { getCurrentUser } from "../../utils/auth";
import type { User, UserRole } from "../../types/shared.types";
import type {
  ViewAccessMode,
  UserFormData,
  FormErrors,
} from "../../types/admin.types";

interface UserManagementTabProps {
  initialLoading?: boolean;
}

const UserManagementTab: React.FC<UserManagementTabProps> = ({
  initialLoading = false,
}) => {
  const { t } = useTranslation(["admin", "common"]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(initialLoading);
  const [showCreateUser, setShowCreateUser] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filterRole, setFilterRole] = useState<string>("all");
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [viewAccessMode, setViewAccessMode] =
    useState<ViewAccessMode>("all_open");
  const [isUpdatingViewMode, setIsUpdatingViewMode] = useState<boolean>(false);
  const [formData, setFormData] = useState<UserFormData>({
    username: "",
    password: "",
    email: "",
    role: "recruiter",
    isActive: true,
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [showInviteModal, setShowInviteModal] = useState<boolean>(false);
  const [inviteData, setInviteData] = useState({
    email: "",
    name: "",
    role: "recruiter",
  });
  const [inviteResult, setInviteResult] = useState<{
    tempPassword: string;
  } | null>(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchUsers(), fetchViewMode()]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    filterUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, searchTerm, filterRole]);

  const fetchUsers = async (): Promise<void> => {
    try {
      const response = (await recruitingAPI.getAdminUsers()) as {
        users?: User[];
      };
      setUsers(response.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchViewMode = async (): Promise<void> => {
    try {
      const response = (await recruitingAPI.getSystemConfig(
        "view_access_mode",
      )) as { data?: { value?: string } };
      const mode = (response.data?.value || "all_open") as ViewAccessMode;
      setViewAccessMode(mode);
    } catch (error) {
      console.error("Error loading view mode:", error);
    }
  };

  const handleViewModeChange = async (mode: ViewAccessMode): Promise<void> => {
    if (mode === viewAccessMode) return;
    setIsUpdatingViewMode(true);
    try {
      await recruitingAPI.setSystemConfig("view_access_mode", mode);
      setViewAccessMode(mode);
      alert(
        t("users.messages.viewModeChanged", {
          mode:
            mode === "all_open"
              ? t("users.viewMode.allOpen")
              : t("users.viewMode.locked"),
        }),
      );
    } catch (error) {
      console.error("Error updating view mode:", error);
      alert(t("users.messages.failed", { action: "update view mode for" }));
    } finally {
      setIsUpdatingViewMode(false);
    }
  };

  const filterUsers = (): void => {
    let filtered = users;

    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.username?.toLowerCase().includes(query) ||
          user.email?.toLowerCase().includes(query),
      );
    }

    if (filterRole !== "all") {
      filtered = filtered.filter((user) => user.role === filterRole);
    }

    setFilteredUsers(filtered);
  };

  const handleCreateUser = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    if (!formData.username || !formData.password || !formData.email) {
      setFormErrors({ general: "Username, password, and email are required" });
      return;
    }

    setLoading(true);
    setFormErrors({});

    try {
      await recruitingAPI.createUser({
        username: formData.username,
        password: formData.password,
        email: formData.email,
        role: formData.role as UserRole,
      } as Partial<User> & { password: string });

      await fetchUsers();
      setShowCreateUser(false);
      setFormData({
        username: "",
        email: "",
        password: "",
        role: "recruiter",
        isActive: true,
      });
      alert(t("users.messages.userCreated"));
    } catch (error) {
      const err = error as {
        response?: { data?: { error?: string; message?: string } };
        message: string;
      };
      console.error("Error creating user:", error);
      setFormErrors({
        general:
          err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to create user",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    setFormErrors({});

    try {
      await recruitingAPI.updateUser(selectedUser!.id, {
        email: formData.email,
        role: formData.role as UserRole,
        is_active: formData.isActive,
      });

      await fetchUsers();
      setShowEditModal(false);
      setSelectedUser(null);
      setFormData({
        username: "",
        email: "",
        password: "",
        role: "recruiter",
        isActive: true,
      });
      alert(t("users.messages.userUpdated"));
    } catch (error) {
      const err = error as {
        response?: { data?: { error?: string; message?: string } };
        message: string;
      };
      console.error("Error updating user:", error);
      setFormErrors({
        general:
          err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to update user",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (
    userId: number,
    username: string,
  ): Promise<void> => {
    if (!window.confirm(t("users.deleteConfirm", { username }))) return;

    setLoading(true);
    try {
      await recruitingAPI.deleteUser(userId);
      await fetchUsers();
      alert(t("users.messages.userDeleted"));
    } catch (error) {
      console.error("Error deleting user:", error);
      alert(t("users.messages.failed", { action: "delete" }));
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    if (!inviteData.email || !inviteData.name) {
      setFormErrors({ general: "Email and name are required" });
      return;
    }
    setLoading(true);
    setFormErrors({});
    try {
      const result = (await api.tenant.inviteUser(inviteData)) as {
        success: boolean;
        user: { tempPassword: string };
      };
      setInviteResult({ tempPassword: result.user.tempPassword });
      await fetchUsers();
    } catch (error) {
      const err = error as {
        response?: { data?: { error?: string } };
        message: string;
      };
      console.error("Error inviting user:", error);
      setFormErrors({
        general: err.response?.data?.error || "Failed to invite user",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (user: User): Promise<void> => {
    const action = user.is_active ? "deactivate" : "activate";
    const confirmMsg = user.is_active
      ? t("users.deactivateConfirm", { username: user.username })
      : t("users.activateConfirm", { username: user.username });
    if (!window.confirm(confirmMsg)) return;
    try {
      if (user.is_active) {
        await api.tenant.deactivateUser(user.id);
      } else {
        await api.tenant.activateUser(user.id);
      }
      await fetchUsers();
    } catch (error) {
      console.error(`Error ${action}ing user:`, error);
      alert(`Failed to ${action} user.`);
    }
  };

  const openEditModal = (user: User): void => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: "",
      role: user.role,
      isActive: user.is_active ?? true,
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  const openCreateModal = (): void => {
    setFormData({
      username: "",
      email: "",
      password: "",
      role: "recruiter",
      isActive: true,
    });
    setFormErrors({});
    setShowCreateUser(true);
  };

  // Show skeleton loading state
  const showSkeletonLoading = loading && users.length === 0;

  return (
    <div className="admin-section">
      {/* View Mode Toggle */}
      <div className="view-mode-section">
        <div className="section-header">
          <h2>{t("users.viewMode.title")}</h2>
        </div>
        <div className="view-mode-toggle">
          <div className="view-mode-buttons">
            <button
              className={`view-mode-btn ${viewAccessMode === "all_open" ? "active" : ""}`}
              onClick={() => handleViewModeChange("all_open")}
              disabled={isUpdatingViewMode}
            >
              {t("users.viewMode.allOpen")}
            </button>
            <button
              className={`view-mode-btn ${viewAccessMode === "locked" ? "active" : ""}`}
              onClick={() => handleViewModeChange("locked")}
              disabled={isUpdatingViewMode}
            >
              {t("users.viewMode.locked")}
            </button>
          </div>
          <small className="view-mode-note">
            {viewAccessMode === "locked"
              ? t("users.viewMode.noteLocked")
              : t("users.viewMode.noteAllOpen")}
          </small>
        </div>
      </div>

      {/* User Management */}
      <div className="section-header">
        <h2>
          <Users size={16} /> {t("users.title")}
        </h2>
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            className="btn-primary"
            onClick={() => {
              setInviteData({ email: "", name: "", role: "recruiter" });
              setInviteResult(null);
              setFormErrors({});
              setShowInviteModal(true);
            }}
          >
            <UserPlus size={16} /> {t("users.inviteUser")}
          </button>
          <button className="btn-primary" onClick={openCreateModal}>
            <Plus size={16} /> {t("users.addUser")}
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="filters-section">
        <div className="search-box">
          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t("users.searchPlaceholder")}
            ariaLabel={t("users.searchPlaceholder")}
            showSearchIcon
            showClearButton
          />
        </div>

        <div className="filter-controls">
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t("users.filters.allRoles")}</option>
            <option value="application_admin">
              {t("users.filters.applicationAdmin")}
            </option>
            <option value="organization_admin">
              {t("users.filters.organizationAdmin")}
            </option>
            <option value="head_of_recruitment">
              {t("users.filters.headOfRecruitment")}
            </option>
            <option value="head_of_sales">
              {t("users.filters.headOfSales")}
            </option>
            <option value="recruiter">{t("users.filters.recruiter")}</option>
            <option value="sales">{t("users.filters.sales")}</option>
            <option value="tech_checker">
              {t("users.filters.techChecker")}
            </option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      {showSkeletonLoading ? (
        <UserTableSkeleton rows={5} />
      ) : (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>{t("users.table.user")}</th>
                <th>{t("users.table.email")}</th>
                <th>{t("users.table.role")}</th>
                <th>{t("users.table.status")}</th>
                <th>{t("users.table.created")}</th>
                <th>{t("users.table.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {(filteredUsers.length > 0 ? filteredUsers : users).map(
                (user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="user-info">
                        <div className="user-avatar">
                          <UserIcon size={16} />
                        </div>
                        <div>
                          <div className="user-name">{user.username}</div>
                          <div className="user-username">@{user.username}</div>
                        </div>
                      </div>
                    </td>
                    <td>{user.email || "N/A"}</td>
                    <td>
                      <span className={`role-badge role-${user.role}`}>
                        {user.role}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`status-badge status-${user.is_active ? "active" : "inactive"}`}
                      >
                        {user.is_active
                          ? t("users.table.active")
                          : t("users.table.inactive")}
                      </span>
                    </td>
                    <td>
                      {user.created_at
                        ? new Date(user.created_at).toLocaleDateString()
                        : "-"}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => openEditModal(user)}
                          title={t("users.table.editUser")}
                        >
                          <Edit size={16} />
                        </button>
                        {user.id !== getCurrentUser()?.id && (
                          <>
                            <button
                              className={`btn btn-secondary btn-sm ${user.is_active ? "" : "delete-btn"}`}
                              onClick={() => handleToggleActive(user)}
                              title={
                                user.is_active
                                  ? t("users.table.deactivateUser")
                                  : t("users.table.activateUser")
                              }
                              style={
                                !user.is_active
                                  ? { color: "#10b981" }
                                  : undefined
                              }
                            >
                              <Power size={16} />
                            </button>
                            <button
                              className="btn btn-secondary btn-sm delete-btn"
                              onClick={() =>
                                handleDeleteUser(user.id, user.username)
                              }
                              title={t("users.table.deleteUser")}
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUser && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{t("users.createModal.title")}</h3>
              <button
                className="modal-close"
                onClick={() => setShowCreateUser(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateUser}>
              {formErrors.general && (
                <div className="error-message general-error">
                  {formErrors.general}
                </div>
              )}
              <div className="form-group">
                <label htmlFor="create-username" className="form-label">
                  {t("users.createModal.username")} *
                </label>
                <input
                  id="create-username"
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={(e) =>
                    setFormData({ ...formData, username: e.target.value })
                  }
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="create-email" className="form-label">
                  {t("users.createModal.email")} *
                </label>
                <input
                  id="create-email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="create-password" className="form-label">
                  {t("users.createModal.password")} *
                </label>
                <input
                  id="create-password"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="form-input"
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="create-role" className="form-label">
                    {t("users.createModal.role")}
                  </label>
                  <select
                    id="create-role"
                    name="role"
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    className="form-input"
                  >
                    <option value="recruiter">Recruiter</option>
                    <option value="sales">Sales</option>
                    <option value="head_of_recruitment">
                      Head of Recruitment
                    </option>
                    <option value="head_of_sales">Head of Sales</option>
                    <option value="tech_checker">Tech Checker</option>
                    <option value="organization_admin">
                      Organization Admin
                    </option>
                    <option value="application_admin">Platform Admin</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="create-status" className="form-label">
                    {t("users.createModal.status")}
                  </label>
                  <select
                    id="create-status"
                    name="isActive"
                    value={String(formData.isActive)}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        isActive: e.target.value === "true",
                      })
                    }
                    className="form-input"
                  >
                    <option value="true">{t("users.table.active")}</option>
                    <option value="false">{t("users.table.inactive")}</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowCreateUser(false)}
                >
                  {t("common:actions.cancel")}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading
                    ? t("users.createModal.creating")
                    : t("users.createModal.create")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{t("users.editModal.title")}</h3>
              <button
                className="modal-close"
                onClick={() => setShowEditModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleEditUser}>
              {formErrors.general && (
                <div className="error-message general-error">
                  {formErrors.general}
                </div>
              )}
              <div className="form-group">
                <label htmlFor="edit-username" className="form-label">
                  {t("users.createModal.username")}
                </label>
                <input
                  id="edit-username"
                  type="text"
                  value={formData.username}
                  className="form-input"
                  disabled
                />
                <small
                  style={{ color: "#666", display: "block", marginTop: "4px" }}
                >
                  {t("users.editModal.usernameDisabled")}
                </small>
              </div>
              <div className="form-group">
                <label htmlFor="edit-email" className="form-label">
                  {t("users.createModal.email")}
                </label>
                <input
                  id="edit-email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-role" className="form-label">
                    {t("users.createModal.role")}
                  </label>
                  <select
                    id="edit-role"
                    name="role"
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    className="form-input"
                  >
                    <option value="recruiter">Recruiter</option>
                    <option value="sales">Sales</option>
                    <option value="head_of_recruitment">
                      Head of Recruitment
                    </option>
                    <option value="head_of_sales">Head of Sales</option>
                    <option value="tech_checker">Tech Checker</option>
                    <option value="organization_admin">
                      Organization Admin
                    </option>
                    <option value="application_admin">Platform Admin</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="edit-status" className="form-label">
                    {t("users.createModal.status")}
                  </label>
                  <select
                    id="edit-status"
                    name="isActive"
                    value={String(formData.isActive)}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        isActive: e.target.value === "true",
                      })
                    }
                    className="form-input"
                  >
                    <option value="true">{t("users.table.active")}</option>
                    <option value="false">{t("users.table.inactive")}</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  {t("common:actions.cancel")}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading
                    ? t("users.editModal.updating")
                    : t("users.editModal.update")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>
                {inviteResult
                  ? t("users.inviteModal.titleSuccess")
                  : t("users.inviteUser")}
              </h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteResult(null);
                }}
              >
                &times;
              </button>
            </div>
            {inviteResult ? (
              <div>
                <p>{t("users.inviteModal.successMessage")}</p>
                <div
                  style={{
                    background: "#f3f4f6",
                    padding: "12px",
                    borderRadius: "6px",
                    marginTop: "8px",
                  }}
                >
                  <div>
                    <strong>{t("users.inviteModal.email")}:</strong>{" "}
                    {inviteData.email}
                  </div>
                  <div>
                    <strong>{t("users.inviteModal.tempPassword")}:</strong>{" "}
                    <code>{inviteResult.tempPassword}</code>
                  </div>
                </div>
                <div className="modal-actions" style={{ marginTop: "16px" }}>
                  <button
                    className="btn-primary"
                    onClick={() => {
                      setShowInviteModal(false);
                      setInviteResult(null);
                    }}
                  >
                    {t("common:actions.done")}
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleInviteUser}>
                {formErrors.general && (
                  <div className="error-message general-error">
                    {formErrors.general}
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="invite-name" className="form-label">
                    {t("users.inviteModal.name")} *
                  </label>
                  <input
                    id="invite-name"
                    type="text"
                    value={inviteData.name}
                    onChange={(e) =>
                      setInviteData({ ...inviteData, name: e.target.value })
                    }
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="invite-email" className="form-label">
                    {t("users.inviteModal.email")} *
                  </label>
                  <input
                    id="invite-email"
                    type="email"
                    value={inviteData.email}
                    onChange={(e) =>
                      setInviteData({ ...inviteData, email: e.target.value })
                    }
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="invite-role" className="form-label">
                    {t("users.inviteModal.role")}
                  </label>
                  <select
                    id="invite-role"
                    value={inviteData.role}
                    onChange={(e) =>
                      setInviteData({ ...inviteData, role: e.target.value })
                    }
                    className="form-input"
                  >
                    <option value="recruiter">Recruiter</option>
                    <option value="sales">Sales</option>
                    <option value="head_of_recruitment">
                      Head of Recruitment
                    </option>
                    <option value="head_of_sales">Head of Sales</option>
                    <option value="tech_checker">Tech Checker</option>
                    <option value="organization_admin">
                      Organization Admin
                    </option>
                    <option value="application_admin">Platform Admin</option>
                  </select>
                </div>
                <small
                  style={{
                    color: "#666",
                    display: "block",
                    marginBottom: "12px",
                  }}
                >
                  {t("users.inviteModal.tempPasswordNote")}
                </small>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowInviteModal(false)}
                  >
                    {t("common:actions.cancel")}
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={loading}
                  >
                    {loading
                      ? t("users.inviteModal.inviting")
                      : t("users.inviteModal.sendInvite")}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementTab;
